import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error';
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ 
  message, 
  type = 'success', 
  duration = 3000, 
  onClose 
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed top-4 right-4 z-[9999] pointer-events-none"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className={`
            bg-card/95 backdrop-blur-sm 
            border shadow-2xl rounded-xl 
            px-4 py-3 
            flex items-center gap-3
            min-w-[300px] max-w-md
            shadow-lg
            pointer-events-auto
            ${type === 'success' 
              ? 'border-green-500/20 text-green-500 shadow-green-500/10' 
              : 'border-red-500/20 text-red-500 shadow-red-500/10'
            }
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {type === 'success' ? (
            <CheckCircle2 className="w-6 h-6" />
          ) : (
            <AlertCircle className="w-6 h-6" />
          )}
          <p className="text-base font-medium flex-1">{message}</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Toast;